/**
 * Portal control-plane client (seam S2) and the module data envelope (seam S8).
 *
 * Contracts are fixed by the dev spec §7. This file holds the TYPES and the
 * fetch wrapper only — the real `/v1/me` consumption is KET-3228 (shell) and
 * `/v1/authz/check` is KET-3229 (permission model).
 */

export const API_BASE = import.meta.env.VITE_PORTAL_API_BASE ?? "/api";

/** Seam S8 — every module data response is wrapped in this envelope. */
export interface FreshnessMeta {
  data_as_of: string | null;
  source: string | null;
  expected_max_age_hours: number | null;
}

export interface Envelope<T> {
  data: T;
  /** Absent meta renders "we can't confirm this data's date" — never implied-fresh (I-6). */
  meta?: FreshnessMeta;
}

/** Seam S2 — GET /v1/me. */
export interface MeResponse {
  /**
   * Whose view this IS — the TARGET while a view-as is open, so the shell never
   * labels someone else's rail with the viewer's own name.
   */
  person: { email: string; display_name: string };
  /**
   * The open view-as, from the server's own record (C9). `null`, not absent,
   * when there is none: an explicit "no" is one less thing to infer from a
   * missing key. The shell reads the mode here rather than remembering it,
   * which is what makes it survive a navigation (UX F5).
   */
  view_as?: { target_email: string; actor_email: string; display_name?: string } | null;
  /**
   * May this person open access administration? Resolved by the API from the
   * same helper the write endpoints use, so the menu and the API can never
   * disagree — a control that refuses on click teaches people to distrust the
   * menu.
   */
  can_administer?: boolean;
  /**
   * May this person change the ROSTER — add or remove someone from the portal?
   *
   * A separate answer from `can_administer`, which an area owner also gets:
   * they set levels inside their own area, while putting someone on the portal
   * at all is estate-wide and is a portal admin's act. One flag for both would
   * either hide the grid from owners or show them controls that refuse on
   * click.
   */
  can_manage_people?: boolean;
  landing_area: string | null;
  areas: Array<{
    area_key: string;
    display_name: string;
    rail_order: number;
    /**
     * Who to ask for edit rights (UX S5). `null` when no owner is recorded —
     * `areas.yaml` leaves `owner_email` null on purpose, because an owner is
     * never inferred from who built an area or who last touched it.
     */
    owner_email: string | null;
    /** The level this person holds in the AREA, whatever a module says. */
    permission: "view" | "edit";
    modules: Array<{
      module_key: string;
      display_name: string;
      tabs: Array<{
        tab_key: string;
        display_name: string;
        tab_order: number;
        /** Docked modules only — where this tab lands inside the framed surface (C11). */
        surface_path?: string | null;
        /**
         * The named group this tab renders under in the bar. Absent or null =
         * the tab renders on its own. Groups replaced the "More" overflow
         * (Damien, 2026-08-26).
         */
        tab_group?: string | null;
      }>;
      permission: "view" | "edit";
      /**
       * `native` or `docked` (C11). Absent is read as native, so a control
       * plane that has not been migrated yet still renders — it just never
       * frames anything, which is the safe direction to fail.
       */
      kind?: "native" | "docked";
      /** The origin the shell frames for a docked module. Null for a native one. */
      surface_url?: string | null;
    }>;
    /**
     * Whether this area's module list is trustworthy.
     *
     * `pending` means the registry could not answer for THIS area — the area
     * still renders and the rest of the rail stays usable, and that one area
     * shows an inline pending row (UX S2 partial). It is deliberately not an
     * empty list: an empty list asserts "this area has no modules", which is a
     * different and possibly false statement.
     */
    modules_status: "ready" | "pending";
    /**
     * Coming-soon names from `areas.yaml`, rendered as muted non-clickable rows.
     * Not modules: nothing opens. Absent from an older API is read as none.
     */
    placeholders?: string[];
  }>;
}

export class PortalApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "PortalApiError";
  }
}

/**
 * Credentialed fetch against the control API. The Cloudflare Access cookie is
 * issued SameSite=None; Secure, so cross-origin calls must send credentials.
 */
export async function portalFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, credentials: "include" });
  if (!res.ok) {
    throw new PortalApiError(`${init.method ?? "GET"} ${path} failed`, res.status);
  }
  return (await res.json()) as T;
}
